export const VERTEX_SHADER_SOURCE = `
attribute vec2 a_position;
varying vec2 v_uv;

void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const SHARED_NOISE_SOURCE = `
float hash(vec2 point) {
  vec3 value = fract(vec3(point.xyx) * 0.1031);
  value += dot(value, value.yzx + 33.33);
  return fract((value.x + value.y) * value.z);
}

vec2 hash2(vec2 point) {
  return vec2(
    hash(point + 17.17),
    hash(point + vec2(41.31, 9.71))
  );
}

float noise(vec2 point) {
  vec2 cell = floor(point);
  vec2 local = fract(point);
  vec2 curve = local * local * (3.0 - 2.0 * local);

  float a = hash(cell);
  float b = hash(cell + vec2(1.0, 0.0));
  float c = hash(cell + vec2(0.0, 1.0));
  float d = hash(cell + vec2(1.0, 1.0));

  return mix(mix(a, b, curve.x), mix(c, d, curve.x), curve.y);
}

float fbm(vec2 point) {
  mat2 turn = mat2(0.8, -0.6, 0.6, 0.8);
  float value = 0.0;
  float amplitude = 0.5;

  for (int i = 0; i < 4; i += 1) {
    value += amplitude * noise(point);
    point = turn * point * 2.03 + 7.17;
    amplitude *= 0.52;
  }

  return value;
}

vec2 curl(vec2 point) {
  float epsilon = 0.035;
  float left = fbm(point - vec2(epsilon, 0.0));
  float right = fbm(point + vec2(epsilon, 0.0));
  float down = fbm(point - vec2(0.0, epsilon));
  float up = fbm(point + vec2(0.0, epsilon));
  return vec2(up - down, left - right) / (2.0 * epsilon);
}

float particleLayer(vec2 point, float scale, float seed) {
  vec2 grid = point * scale;
  vec2 cell = floor(grid);
  vec2 local = fract(grid);
  float value = 0.0;

  for (int y = -1; y <= 1; y += 1) {
    for (int x = -1; x <= 1; x += 1) {
      vec2 neighbor = vec2(float(x), float(y));
      vec2 particleSeed = cell + neighbor + vec2(seed);
      vec2 random = hash2(particleSeed);
      vec2 center = random;
      vec2 drift = vec2(
        sin(u_time * (0.14 + random.x * 0.18) + random.y * 6.2831),
        cos(u_time * (0.12 + random.y * 0.2) + random.x * 6.2831)
      ) * 0.034;
      vec2 delta = local - (neighbor + center + drift);
      vec2 shape = delta * vec2(1.0 + random.x * 0.55, 1.18 + random.y * 0.85);
      float size = mix(36.0, 72.0, random.y);
      float particle = exp(-dot(shape, shape) * size);
      float flicker = 0.78 + 0.22 * sin(u_time * (0.2 + random.x * 0.28) + random.y * 6.2831);

      value += particle * flicker * (0.36 + random.x * 0.64);
    }
  }

  return value;
}
`;

export const SIMULATION_SHADER_SOURCE = `
precision mediump float;

uniform vec2 u_resolution;
uniform float u_time;
uniform sampler2D u_state;
uniform float u_injection_gain;
uniform vec2 u_pointer;
uniform vec2 u_velocity;
uniform float u_pointer_strength;
uniform float u_wake;
uniform vec4 u_trail[12];
uniform vec2 u_trail_motion[12];
varying vec2 v_uv;

${SHARED_NOISE_SOURCE}

vec2 decodeVelocity(vec4 state) {
  return (state.gb - 0.5) * 2.0;
}

void injectStroke(
  vec2 point,
  vec2 origin,
  vec2 motion,
  float strength,
  inout float density,
  inout float dust,
  inout vec2 velocity
) {
  vec2 direction = normalize(motion + vec2(0.001, 0.0));
  vec2 normal = vec2(-direction.y, direction.x);
  vec2 delta = point - origin;
  float speed = clamp(length(motion), 0.0, 1.8);
  float along = dot(delta, direction);
  float across = dot(delta, normal);
  float width = mix(0.011, 0.031, clamp(speed * 0.62, 0.0, 1.0));
  float leadFeather = 1.0 - smoothstep(0.025, 0.22, along);
  float narrowChannel = exp(-(across * across) / (width * width * 1.18))
    * exp(-abs(along) * 8.0)
    * leadFeather;
  float sideDistance = abs(across) - width * 1.8;
  float sideRibbon = exp(-(sideDistance * sideDistance) / (width * width * 1.8))
    * exp(-abs(along) * 4.8)
    * leadFeather;
  float tail = exp(-(across * across) / (width * width * 6.4))
    * exp(along * 3.1)
    * (1.0 - smoothstep(-0.025, 0.32, along));
  float wavePhase = -along * mix(18.0, 28.0, clamp(speed, 0.0, 1.0))
    + across * 16.0
    - u_time * 1.8;
  float wave = 0.5 + 0.5 * sin(wavePhase);
  float wavePacket = tail * (0.32 + 0.68 * wave) * smoothstep(0.05, 0.55, speed);
  float eddy = exp(-dot(delta, delta) * 62.0);
  vec2 eddyDirection = vec2(-delta.y, delta.x) / (length(delta) + 0.004);
  float signedSide = sign(across + 0.0001);

  density += strength * (sideRibbon * 0.024 + tail * 0.02 + wavePacket * 0.012 - narrowChannel * 0.022);
  dust += strength * (sideRibbon * 0.12 + tail * 0.098 + wavePacket * 0.075 + eddy * 0.034);
  velocity += direction * tail * strength * 0.105;
  velocity += normal * signedSide * sideRibbon * strength * 0.072;
  velocity += normal * sin(wavePhase) * wavePacket * strength * 0.038;
  velocity += eddyDirection * eddy * strength * 0.046;
}

void main() {
  vec2 uv = v_uv;
  vec2 pixel = 1.0 / u_resolution;
  vec2 aspect = vec2(u_resolution.x / u_resolution.y, 1.0);
  vec4 centerState = texture2D(u_state, uv);
  vec2 centerVelocity = decodeVelocity(centerState);
  vec2 ambientCurl = curl(uv * vec2(3.6, 2.4) + u_time * 0.055) * 0.004;
  vec2 advectUv = clamp(uv - centerVelocity * 0.024 - ambientCurl, vec2(0.001), vec2(0.999));
  vec4 advected = texture2D(u_state, advectUv);
  vec4 north = texture2D(u_state, advectUv + vec2(0.0, pixel.y));
  vec4 south = texture2D(u_state, advectUv - vec2(0.0, pixel.y));
  vec4 east = texture2D(u_state, advectUv + vec2(pixel.x, 0.0));
  vec4 west = texture2D(u_state, advectUv - vec2(pixel.x, 0.0));
  vec4 softened = (north + south + east + west) * 0.25;
  vec4 state = mix(advected, softened, 0.09);
  float density = state.r * 0.987;
  float dust = state.a * 0.993;
  vec2 velocity = decodeVelocity(state) * 0.985;
  vec2 densityGradient = vec2(east.r - west.r, north.r - south.r);
  vec2 dustGradient = vec2(east.a - west.a, north.a - south.a);
  vec2 northVelocity = decodeVelocity(north);
  vec2 southVelocity = decodeVelocity(south);
  vec2 eastVelocity = decodeVelocity(east);
  vec2 westVelocity = decodeVelocity(west);
  float localCurl = (eastVelocity.y - westVelocity.y) - (northVelocity.x - southVelocity.x);
  vec2 edgeRoll = vec2(densityGradient.y + dustGradient.y, -densityGradient.x - dustGradient.x);
  vec2 point = (uv - 0.5) * aspect;
  vec2 pointerPoint = (u_pointer - 0.5) * aspect;
  vec2 pointerMotion = vec2(u_velocity.x * aspect.x, u_velocity.y);
  float pointerStrength = u_injection_gain
    * u_pointer_strength
    * clamp(length(pointerMotion) * 0.82 + u_wake * 0.25, 0.0, 1.0);

  injectStroke(point, pointerPoint, pointerMotion, pointerStrength, density, dust, velocity);

  for (int i = 0; i < 12; i += 1) {
    vec4 trail = u_trail[i];
    vec2 trailPoint = (trail.xy - 0.5) * aspect;
    vec2 trailMotion = vec2(u_trail_motion[i].x * aspect.x, u_trail_motion[i].y);
    float trailStrength = u_injection_gain * trail.w * exp(-trail.z * 0.13);
    injectStroke(point, trailPoint, trailMotion, trailStrength, density, dust, velocity);
  }

  velocity += curl(uv * 7.0 + density * 1.5 + u_time * 0.075) * density * 0.009;
  velocity += edgeRoll * (0.044 + dust * 0.02);
  velocity += vec2(localCurl * -densityGradient.y, localCurl * densityGradient.x) * 0.032;
  velocity -= densityGradient * 0.02;
  density = clamp(density, 0.0, 1.0);
  dust = clamp(dust, 0.0, 1.0);
  velocity = clamp(velocity, vec2(-0.46), vec2(0.46));

  gl_FragColor = vec4(density, velocity * 0.5 + 0.5, dust);
}
`;

export const FRAGMENT_SHADER_SOURCE = `
precision mediump float;

uniform vec2 u_resolution;
uniform float u_time;
uniform vec3 u_bg;
uniform vec3 u_haze;
uniform vec3 u_wisp;
uniform vec3 u_accent;
uniform sampler2D u_state;
uniform vec2 u_state_resolution;
uniform vec2 u_pointer;
uniform vec2 u_velocity;
uniform float u_pointer_strength;
varying vec2 v_uv;

${SHARED_NOISE_SOURCE}

vec2 decodeVelocity(vec4 state) {
  return (state.gb - 0.5) * 2.0;
}

float bayer4(vec2 position) {
  vec2 pixel = mod(floor(position), 4.0);
  float index = 0.0;

  if (pixel.y < 1.0) {
    if (pixel.x < 1.0) index = 0.0;
    else if (pixel.x < 2.0) index = 8.0;
    else if (pixel.x < 3.0) index = 2.0;
    else index = 10.0;
  } else if (pixel.y < 2.0) {
    if (pixel.x < 1.0) index = 12.0;
    else if (pixel.x < 2.0) index = 4.0;
    else if (pixel.x < 3.0) index = 14.0;
    else index = 6.0;
  } else if (pixel.y < 3.0) {
    if (pixel.x < 1.0) index = 3.0;
    else if (pixel.x < 2.0) index = 11.0;
    else if (pixel.x < 3.0) index = 1.0;
    else index = 9.0;
  } else {
    if (pixel.x < 1.0) index = 15.0;
    else if (pixel.x < 2.0) index = 7.0;
    else if (pixel.x < 3.0) index = 13.0;
    else index = 5.0;
  }

  return (index + 0.5) / 16.0;
}

vec3 applyDither(vec3 color, vec2 position) {
  float threshold = bayer4(position * 0.5) - 0.5;
  float luminance = dot(color, vec3(0.299, 0.587, 0.114));
  float visibility = smoothstep(0.018, 0.18, luminance);
  float levels = mix(10.0, 18.0, visibility);
  vec3 patterned = color + threshold * 0.02 * visibility;
  vec3 dithered = floor(color * levels + threshold * 1.35) / levels;

  return clamp(mix(patterned, dithered, 0.62), 0.0, 1.0);
}

vec3 renderLoginColor(vec2 uv) {
  vec2 aspect = vec2(u_resolution.x / u_resolution.y, 1.0);
  vec2 statePixel = 1.0 / u_state_resolution;
  vec4 state = texture2D(u_state, uv);
  vec4 stateNorth = texture2D(u_state, uv + vec2(0.0, statePixel.y * 1.5));
  vec4 stateSouth = texture2D(u_state, uv - vec2(0.0, statePixel.y * 1.5));
  vec4 stateEast = texture2D(u_state, uv + vec2(statePixel.x * 1.5, 0.0));
  vec4 stateWest = texture2D(u_state, uv - vec2(statePixel.x * 1.5, 0.0));
  vec2 flow = decodeVelocity(state);
  float density = pow(state.r, 1.32);
  float dust = state.a;
  float time = u_time * 0.06;
  vec2 point = (uv - 0.5) * aspect;
  vec2 flowPoint = point + vec2(flow.x * aspect.x, flow.y) * (0.36 + density * 0.42);
  vec2 longCurl = curl(flowPoint * 1.1 + vec2(time * 0.7, -time * 0.42));
  vec2 fineCurl = curl(flowPoint * 3.2 - vec2(time * 0.3, time * 0.52));
  vec2 smokePoint = flowPoint + longCurl * 0.17 + fineCurl * (0.028 + density * 0.042);
  vec2 particlePoint = uv + flow * (0.18 + density * 0.2) + longCurl * 0.028;
  float ambient = fbm(smokePoint * 1.05 + vec2(time * 0.38, -time * 0.2));
  float slowSheet = fbm(smokePoint * 2.3 - vec2(time * 0.54, time * 0.26));
  float thread = fbm(smokePoint * 5.8 + fineCurl * 0.36 - time);
  float particlesA = particleLayer(particlePoint + fineCurl * 0.018, 28.0, 2.7);
  float particlesB = particleLayer(particlePoint * 1.27 + longCurl * 0.032, 48.0, 9.4);
  float wake = smoothstep(0.045, 0.66, density + dust * 0.34);
  float waveTrain = 0.5 + 0.5 * sin(
    slowSheet * 4.4
    + length(flowPoint + longCurl * 0.15) * 13.0
    - time * 5.4
    + flow.x * 3.0
  );
  float edge = length(vec2(stateEast.a - stateWest.a, stateNorth.a - stateSouth.a)) * 0.56
    + length(vec2(stateEast.r - stateWest.r, stateNorth.r - stateSouth.r)) * 0.28;
  float granularSmoke = smoothstep(0.16, 1.22, particlesA * 0.48 + particlesB * 0.32);
  float waveFilament = smoothstep(0.46, 0.86, slowSheet + thread * 0.18 + wake * 0.12 + edge * 0.34)
    + smoothstep(0.42, 0.9, waveTrain) * wake * 0.12;
  float fieldMist = smoothstep(0.32, 0.86, ambient + wake * 0.16);
  float radius = length(point + longCurl * 0.08);
  float vignette = smoothstep(1.1, 0.24, length(point * vec2(0.72, 1.0)));

  fieldMist *= 0.16 + vignette * 0.24;
  granularSmoke *= 0.11 + wake * 0.54 + dust * 0.4;
  waveFilament *= 0.1 + wake * 0.42 + smoothstep(0.025, 0.2, edge) * 0.1;

  vec3 color = mix(u_bg, u_haze, fieldMist + wake * 0.045 + granularSmoke * 0.1);
  color = mix(color, u_wisp, waveFilament * 0.17 + granularSmoke * 0.36);
  color += u_accent * wake * 0.011;
  color *= 0.62 + vignette * 0.42;

  return color;
}

vec2 applyLensDistortion(vec2 uv) {
  vec2 aspect = vec2(u_resolution.x / u_resolution.y, 1.0);
  vec2 framePoint = uv - 0.5;
  vec2 lensPoint = framePoint * aspect;
  vec2 edgePoint = abs(framePoint) * 2.0;
  float edgeMask = smoothstep(0.46, 0.94, max(edgePoint.x, edgePoint.y));
  float radius = length(lensPoint);
  float radius2 = radius * radius;
  float organicRipple = (fbm(lensPoint * 2.1 + u_time * 0.035) - 0.5) * 0.018;
  float barrel = edgeMask * (radius2 * 0.058 + radius2 * radius2 * 0.026 + organicRipple);
  vec2 edgePull = normalize(lensPoint + vec2(0.0001, 0.0)) * barrel;
  vec2 bowedPoint = lensPoint + edgePull;

  return clamp(0.5 + bowedPoint / aspect, vec2(0.001), vec2(0.999));
}

vec2 applyVhsWarp(vec2 uv, vec2 screenUv, vec2 position) {
  float frame = floor(u_time * 18.0);
  float coarseLine = floor(position.y * 0.18);
  float fineLine = floor(position.y * 0.78);
  float coarseNoise = hash(vec2(coarseLine, frame));
  float fineNoise = hash(vec2(fineLine, frame * 1.7));
  float trackingPosition = fract(screenUv.y * 1.35 - u_time * 0.105);
  float trackingBand = smoothstep(0.11, 0.0, abs(trackingPosition - 0.5));
  float edgeSignal = smoothstep(0.64, 1.0, max(abs(screenUv.x - 0.5), abs(screenUv.y - 0.5)) * 2.0);
  float headSwitch = smoothstep(0.085, 0.0, screenUv.y);
  float wobble = sin(screenUv.y * 58.0 + u_time * 5.6) * 0.0012;
  float rowTear = (coarseNoise - 0.5) * (0.0015 + trackingBand * 0.008 + edgeSignal * 0.004);
  float rowStatic = (fineNoise - 0.5) * (0.0008 + edgeSignal * 0.0015);
  vec2 warpedUv = uv + vec2(wobble + rowTear + rowStatic + headSwitch * 0.006, 0.0);

  return clamp(warpedUv, vec2(0.001), vec2(0.999));
}

vec3 applyChromaticAberration(vec3 color, vec2 uv) {
  vec2 aspect = vec2(u_resolution.x / u_resolution.y, 1.0);
  vec2 statePixel = 1.0 / u_state_resolution;
  vec4 state = texture2D(u_state, uv);
  vec4 stateNorth = texture2D(u_state, uv + vec2(0.0, statePixel.y * 2.0));
  vec4 stateSouth = texture2D(u_state, uv - vec2(0.0, statePixel.y * 2.0));
  vec4 stateEast = texture2D(u_state, uv + vec2(statePixel.x * 2.0, 0.0));
  vec4 stateWest = texture2D(u_state, uv - vec2(statePixel.x * 2.0, 0.0));
  vec2 flow = decodeVelocity(state);
  vec2 dustGradient = vec2(stateEast.a - stateWest.a, stateNorth.a - stateSouth.a);
  vec2 densityGradient = vec2(stateEast.r - stateWest.r, stateNorth.r - stateSouth.r);
  vec2 swirlNormal = vec2(dustGradient.y + densityGradient.y, -dustGradient.x - densityGradient.x);
  float density = pow(state.r, 1.08);
  float dust = state.a;
  float edge = length(dustGradient) * 0.82 + length(densityGradient) * 0.48;
  float flowEnergy = clamp(length(flow) * 1.6, 0.0, 1.0);
  vec2 point = (uv - 0.5) * aspect;
  vec2 cursorPoint = (u_pointer - 0.5) * aspect;
  vec2 cursorDelta = point - cursorPoint;
  vec2 motion = vec2(u_velocity.x * aspect.x, u_velocity.y);
  float speed = clamp(length(motion) * 0.58, 0.0, 1.0);
  float motionPulse = clamp(u_pointer_strength * 0.72 + speed * 0.58, 0.0, 1.0);
  float cursorFalloff = exp(-dot(cursorDelta, cursorDelta) * 20.0);
  float rippleFalloff = exp(-dot(cursorDelta, cursorDelta) * 6.2);
  float activeWake = motionPulse * (cursorFalloff * 0.88 + rippleFalloff * 0.2);
  float swirlWake = smoothstep(0.018, 0.36, dust * 0.76 + density * 0.44 + edge * 0.72 + flowEnergy * 0.18);
  float lingeringWake = swirlWake * (0.16 + dust * 0.68 + density * 0.26 + edge * 0.22);
  float aberration = clamp(activeWake + lingeringWake, 0.0, 1.0);

  if (aberration <= 0.001) {
    return color;
  }

  vec2 direction = normalize(
    flow * 1.08
    + swirlNormal * 0.64
    + motion * activeWake * 0.48
    + cursorDelta * cursorFalloff * 0.16
    + vec2(0.0001, 0.0)
  );
  float organicOffset = 0.0025 + aberration * 0.017 + edge * 0.009;
  vec2 uvOffset = vec2(direction.x / aspect.x, direction.y) * organicOffset;
  vec2 redUv = clamp(uv + uvOffset, vec2(0.001), vec2(0.999));
  vec2 blueUv = clamp(uv - uvOffset, vec2(0.001), vec2(0.999));
  vec3 splitColor = vec3(
    renderLoginColor(redUv).r,
    color.g,
    renderLoginColor(blueUv).b
  );
  vec3 cursorFringe = vec3(0.045, 0.0, -0.036) * activeWake * cursorFalloff;
  vec3 swirlFringe = vec3(0.018, 0.0, -0.014) * lingeringWake * (0.35 + edge);
  splitColor = clamp(splitColor + cursorFringe + swirlFringe, 0.0, 1.0);

  return mix(color, splitColor, clamp(activeWake * 1.2 + lingeringWake * 0.82, 0.0, 0.92));
}

vec3 applyVhsEffect(vec3 color, vec2 uv, vec2 position) {
  vec2 pixel = position / u_resolution;
  float frame = floor(u_time * 24.0);
  float line = floor(position.y * 0.72);
  float lineNoise = hash(vec2(line, frame));
  float nextLineNoise = hash(vec2(line + 1.0, frame));
  float signalNoise = (lineNoise - 0.5) * 0.018;
  float lineJitter = (lineNoise - nextLineNoise) * 0.006;
  float scanline = 0.5 + 0.5 * sin(position.y * 2.42 + u_time * 18.0);
  float interlace = step(0.5, fract((position.y + frame) * 0.5));
  float trackingPosition = fract(uv.y * 1.45 - u_time * 0.115);
  float trackingBand = smoothstep(0.14, 0.0, abs(trackingPosition - 0.5));
  float headSwitch = smoothstep(0.06, 0.0, uv.y)
    * (0.35 + 0.65 * hash(vec2(frame, floor(position.x * 0.02))));
  float edgeSignal = smoothstep(0.72, 1.0, max(abs(pixel.x - 0.5), abs(pixel.y - 0.5)) * 2.0);
  float tapeNoise = hash(vec2(floor(position.x * 0.45 + lineJitter * 240.0), line + frame * 3.0)) - 0.5;
  float luma = dot(color, vec3(0.299, 0.587, 0.114));
  vec3 bleed = vec3(
    color.r + signalNoise * 1.1,
    color.g + signalNoise * 0.36,
    color.b - signalNoise * 0.72
  );

  color = mix(color, bleed, 0.38 + edgeSignal * 0.2);
  color *= 0.95 + scanline * 0.065 + interlace * 0.018;
  color += vec3(tapeNoise) * (0.012 + trackingBand * 0.024 + edgeSignal * 0.01);
  color += vec3(0.022, 0.018, -0.012) * trackingBand * (0.35 + luma);
  color -= headSwitch * vec3(0.04, 0.035, 0.028);

  return clamp(color, 0.0, 1.0);
}

void main() {
  vec2 uv = v_uv;
  vec2 lensUv = applyLensDistortion(uv);
  vec2 vhsUv = applyVhsWarp(lensUv, uv, gl_FragCoord.xy);
  vec3 color = applyChromaticAberration(renderLoginColor(vhsUv), vhsUv);
  color = applyVhsEffect(color, uv, gl_FragCoord.xy);

  float grain = hash(gl_FragCoord.xy + u_time) - 0.5;
  color += grain * 0.007;
  color = applyDither(color, gl_FragCoord.xy);

  gl_FragColor = vec4(color, 1.0);
}
`;

export const FALLBACK_BACKGROUND = [
  'radial-gradient(circle at 50% 45%, color-mix(in srgb, var(--accent-primary) 20%, transparent), transparent 42%)',
  'radial-gradient(circle at 38% 34%, color-mix(in srgb, var(--text-secondary) 18%, transparent), transparent 36%)',
  'var(--bg-app)',
].join(', ');
